/**
 * <lp-pdp-subscription>: one-time vs Subscribe & Save picker for the default PDP.
 *
 * Keeps the hidden `selling_plan` input (tied to #lp-product-form) in sync with
 * the shopper's choice. assets/cart-add.js reads that input and attaches the
 * plan to the main line item on add to cart. Rebuilds the frequency list and
 * prices when the variant changes, and hides itself for variants with no plans.
 *
 * Also drives the price block above the form (.lp-pdp__price-row): the plan
 * price with the one-time price struck through while subscribing, the
 * variant's own price/compare-at for one-time purchases.
 *
 * Plans with the same name (e.g. the same frequency offered by more than one
 * selling plan group) are collapsed so each frequency is listed once.
 */
if (!customElements.get('lp-pdp-subscription')) {
  customElements.define(
    'lp-pdp-subscription',
    class LpPdpSubscription extends HTMLElement {
      connectedCallback() {
        try {
          this.data = JSON.parse(this.querySelector('[data-sub-data]')?.textContent || '{}');
        } catch (e) {
          console.error('lp-pdp-subscription: invalid plan data', e);
          return;
        }

        this.planInput = this.querySelector('[data-sub-plan-input]');
        this.typeRadios = Array.from(this.querySelectorAll('[data-sub-type]'));
        this.freqSelect = this.querySelector('[data-sub-freq-select]');
        this.freqButtons = this.querySelector('[data-sub-freq-buttons]');
        this.form = document.getElementById('lp-product-form');
        this.variantInput = this.form?.querySelector('input[name="id"]');
        this.requiresPlan = this.dataset.requiresPlan === 'true';
        this.selectedPlanId = null;

        const defaultType = this.requiresPlan ? 'subscribe' : this.dataset.default || 'subscribe';
        const defaultRadio = this.typeRadios.find((r) => r.value === defaultType) || this.typeRadios[0];
        if (defaultRadio) defaultRadio.checked = true;

        this.typeRadios.forEach((radio) => radio.addEventListener('change', () => this.sync()));
        this.freqSelect?.addEventListener('change', () => {
          this.selectedPlanId = Number(this.freqSelect.value);
          this.selectType('subscribe');
        });

        // The gallery/option pickers dispatch a bubbling `change` on the variant
        // id input; option radios change first, so read the id on the next frame.
        // Our own radios/select live in the same form, so only re-render when the
        // variant actually changed.
        this.onVariantChange = () =>
          requestAnimationFrame(() => {
            if (this.variantId !== this.renderedVariantId) this.render();
          });
        this.form?.addEventListener('change', this.onVariantChange);

        this.render();

        // The gallery script writes the variant price into the price block on
        // DOMContentLoaded; re-apply ours after it so the subscribe price sticks.
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.sync(), { once: true });
        }
      }

      disconnectedCallback() {
        this.form?.removeEventListener('change', this.onVariantChange);
      }

      get variantId() {
        return Number(this.variantInput?.value) || this.data.currentVariantId;
      }

      get variant() {
        return this.data.variants?.[this.variantId];
      }

      get type() {
        return this.typeRadios.find((r) => r.checked)?.value || 'subscribe';
      }

      selectType(value) {
        const radio = this.typeRadios.find((r) => r.value === value);
        if (radio && !radio.checked) radio.checked = true;
        this.sync();
      }

      /* Rebuild plans, prices and badge for the current variant. */
      render() {
        const variant = this.variant;
        const plans = this.uniquePlans(variant?.plans || []);
        this.renderedVariantId = this.variantId;

        this.hidden = plans.length === 0;
        if (!plans.length) {
          this.planInput.value = '';
          return;
        }

        if (!plans.some((p) => p.id === this.selectedPlanId)) {
          this.selectedPlanId = plans[0].id;
        }

        this.renderFrequencies(plans);

        // A single frequency needs no picker; it's shown in the subtext instead.
        const frequency = this.querySelector('[data-sub-frequency]');
        if (frequency) frequency.hidden = plans.length < 2;

        const onetimePrice = this.querySelector('[data-sub-onetime-price]');
        if (onetimePrice) onetimePrice.textContent = variant.priceFormatted;

        this.sync();
      }

      /* Keep the first plan for each frequency name. */
      uniquePlans(plans) {
        const seen = new Set();
        return plans.filter((plan) => {
          const key = String(plan.name).trim().toLowerCase();
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      }

      renderFrequencies(plans) {
        if (this.freqSelect) {
          this.freqSelect.innerHTML = '';
          plans.forEach((plan) => {
            const option = document.createElement('option');
            option.value = plan.id;
            option.textContent = plan.name;
            option.selected = plan.id === this.selectedPlanId;
            this.freqSelect.appendChild(option);
          });
        }

        if (this.freqButtons) {
          const groupName = this.freqButtons.dataset.groupName;
          this.freqButtons.innerHTML = '';
          plans.forEach((plan) => {
            const label = document.createElement('label');
            label.className = 'lp-sub__freq-button';

            const input = document.createElement('input');
            input.type = 'radio';
            input.name = groupName;
            input.value = plan.id;
            input.className = 'lp-sub__visually-hidden';
            input.checked = plan.id === this.selectedPlanId;
            input.addEventListener('change', () => {
              this.selectedPlanId = plan.id;
              this.selectType('subscribe');
            });

            const text = document.createElement('span');
            text.textContent = plan.name;

            label.append(input, text);
            this.freqButtons.appendChild(label);
          });
        }
      }

      /* Push the current choice to the hidden input, prices, badge and selected state. */
      sync() {
        const variant = this.variant;
        const plan = variant?.plans.find((p) => p.id === this.selectedPlanId);
        const subscribing = this.type === 'subscribe' && plan;

        this.planInput.value = subscribing ? plan.id : '';
        // Read by assets/cart-add.js for the "Frequency" line item property.
        this.planInput.dataset.frequency = subscribing ? plan.name : '';
        this.updateMainPrice(variant, subscribing ? plan : null);

        this.querySelectorAll('[data-sub-option]').forEach((el) => {
          el.classList.toggle('is-selected', el.dataset.subOption === this.type);
        });

        if (!plan) return;

        const price = this.querySelector('[data-sub-price]');
        if (price) price.textContent = plan.priceFormatted;

        const compare = this.querySelector('[data-sub-compare]');
        if (compare) {
          const showCompare = plan.compareAtPrice > plan.price;
          compare.hidden = !showCompare;
          compare.textContent = showCompare ? plan.compareAtFormatted : '';
        }

        // Savings are measured against the one-time price, not compare-at.
        const percent = variant.price > 0 ? Math.round(((variant.price - plan.price) / variant.price) * 100) : 0;

        const badge = this.querySelector('[data-sub-badge]');
        if (badge) {
          badge.hidden = percent <= 0;
          badge.textContent = (this.dataset.badgeTemplate || 'Save [percent]%').replace('[percent]', percent);
        }

        const subtext = this.querySelector('[data-sub-subtext]');
        if (subtext) subtext.textContent = subtext.dataset.template.replace('[frequency]', plan.name);

        const benefits = this.querySelector('[data-sub-benefits]');
        if (benefits) benefits.innerHTML = benefits.dataset.template.replaceAll('[percent]', percent);
      }

      /*
       * Write the selected price into the PDP price block. Mirrors updateMainPrice in
       * component-lp-pdp-gallery.js, and like it writes through ShopliftPrice so an
       * active price test keeps its adjustment.
       */
      updateMainPrice(variant, plan) {
        const section = this.closest('.default-pdp-info');
        if (!section || !variant || !window.ShopliftPrice) return;
        // The sales-final section manages its own price markup.
        if (section.querySelector('[id^="lp-sf-sale-price-"]')) return;

        const price = plan ? plan.price : variant.price;
        // Subscribing compares against the one-time price (or its compare-at, if higher).
        const compareAt = plan ? Math.max(variant.compareAtPrice || 0, variant.price) : variant.compareAtPrice || 0;
        const onSale = compareAt > price;
        const currency = section.dataset.currency || window.Shopify?.currency?.active || 'USD';
        const format = (cents) => {
          try {
            return new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency,
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            }).format(cents / 100);
          } catch (e) {
            return (cents / 100).toFixed(2).replace(/\.00$/, '') + ' ' + currency;
          }
        };
        const seed = (el, attr, fallback) => {
          const n = Number(el?.getAttribute(attr));
          return el?.getAttribute(attr) && Number.isFinite(n) ? n : fallback;
        };

        section.querySelectorAll('.lp-pdp__price-row').forEach((row) => {
          const priceEl = row.querySelector('.lp-pdp__price');
          const compareEl = row.querySelector('.lp-pdp__compare-price');
          const offEl = row.querySelector('.lp-pdp__price-off');
          const priceSeed = seed(priceEl, 'data-lp-rendered-price', variant.price);
          const compareSeed = seed(compareEl, 'data-lp-rendered-compare', compareAt);

          if (priceEl) window.ShopliftPrice.write(priceEl, price, format, priceSeed);

          if (compareEl) {
            if (onSale) window.ShopliftPrice.write(compareEl, compareAt, format, compareSeed);
            compareEl.hidden = !onSale;
          }

          if (!offEl) return;
          const shownPrice = price + window.ShopliftPrice.adjustment(priceEl, priceSeed);
          const shownCompare = compareAt + window.ShopliftPrice.adjustment(compareEl, compareSeed);
          const pct = onSale && shownCompare > 0 ? Math.round(((shownCompare - shownPrice) / shownCompare) * 100) : 0;
          offEl.hidden = pct <= 0;
          if (pct > 0) {
            const prefix = (offEl.dataset.offPrefix || '').trim();
            const suffix = (offEl.dataset.offSuffix || '').trim();
            offEl.textContent = [prefix, pct + '%', suffix].filter(Boolean).join(' ');
          }
        });
      }
    }
  );
}
